import React, { Fragment } from 'react';

import { useStyletron } from '../../../Themes';
import { RightContainerHeaderType } from '../types';
import SearchBar from './SearchBar';
import { TooltipButton } from './TooltipButton';
import LanguageSelector from './LanguageSelector';
import Avatar from './Avatar';
import { useKeyboardShortcut } from '../../../Lib/KeyboardShortcuts';

const HeaderSubContainerRight = ({ className, ...props }: any) => {
    const [css]: any = useStyletron();

    const basicClassname = `${css({
        display: 'flex',
        height: '100%',
        alignItems: 'center',
    })}`;
    const headerRightSubContainer = className ? `${className} ${basicClassname}` : basicClassname;

    return (
        // todo-a11y: 1st section: utilities - interface controls?
        <section
            {...props}
            aria-label="utilities - global controls" // todo-a11y: 2nd section: dynamic labeling?
            className={headerRightSubContainer}
        ></section>
    );
};

type ActiveKeyDrawerType = {
    setActiveKeyDrawer: (arg: any) => void;
    setActiveKey: (arg: string) => void;
}

function RightContainer(props: RightContainerHeaderType & ActiveKeyDrawerType) {
    const {
        rightLayout,
        search,
        onSearchType,
        customUtilities,
        searchButton,
        onClickSearchButton,
        connectivityStatus,
        onClickConnectivityStatus,
        languageSelector,
        onClickLanguageSelector,
        voiceCommand,
        onClickVoiceCommand,
        themeSwitch,
        onThemeSwitch,
        secondWindow,
        onClickSecondWindow,
        keyboardShortcut = false,
        onClickKeyboardShortcut,
        settings,
        onClickSettings,
        help,
        onClickHelp,
        globalNavigation,
        onClickGlobalNavigation,
        username,
        onClickAvatar,
        setActiveKeyDrawer,
        setActiveKey
    } = props;

    const helpButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const globalNavigationButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const settingsButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const keyboardShortcutButtonRef = React.useRef<HTMLButtonElement | null>(null);

    if (keyboardShortcut) {
        useKeyboardShortcut({
            id: 'HEADER_TOGGLE_SHORTCUTS_DRAWER',
            handler: () => {
                keyboardShortcutButtonRef.current?.click();
            },
        });

        useKeyboardShortcut({
            id: 'HEADER_HELP',
            handler: () => {
                helpButtonRef.current?.click();
            },
        });

        useKeyboardShortcut({
            id: 'HEADER_GLOBAL_NAVIGATION',
            handler: () => {
                globalNavigationButtonRef.current?.click();
            },
        });
    }

    const handleKeyboardShortcutsClick = () => {
        setActiveKey('keyboardShortcuts');
        setActiveKeyDrawer(state => {
            return { ...state, keyboardShortcuts: true }
        });
    };

    const handleSettingsClick = () => {
        setActiveKey('settings');
        setActiveKeyDrawer(state => ({ ...state, settings: true }));
    };

    const handleHelpClick = () => {
        setActiveKey('help');
        setActiveKeyDrawer(state => ({ ...state, help: true }));
    };

    const handleGlobalNavigationClick = () => {
        setActiveKey('globalNavigation');
        setActiveKeyDrawer(state => ({ ...state, globalNavigation: true }));
    };

    const switchTheme = (event: any) => {
        const target = event.target.closest('#theme-switcher-control');
        const targetClasses = target.getAttribute('class');
        let targetClass;
        if (targetClasses === null) {
            targetClass = 'dark';
        } else {
            const classesWithSwitchedThemeValue = targetClasses.includes('light')
                ? targetClasses.replace('light', 'dark')
                : targetClasses.replace('dark', 'light');
            targetClass = classesWithSwitchedThemeValue;
        }
        target && target.setAttribute('class', targetClass);
    };

    return <HeaderSubContainerRight>
        {rightLayout}
        {search && <SearchBar onSearchType={onSearchType} keyboardShortcut={keyboardShortcut} />}
        {customUtilities &&
            customUtilities.map((utility, i) => {
                const key = new Date().toDateString() + i;
                return <Fragment key={key}>{utility.content}</Fragment>;
            })}
        {searchButton && (
            <TooltipButton
                icon="search"
                tooltip="open/close global search"
                ariaLabel="open/close global search"
                onClickTooltipButton={(event: MouseEvent) => {
                    onClickSearchButton && onClickSearchButton(event);
                }}
            />
        )}
        {connectivityStatus && (
            <TooltipButton
                icon="cloud_off"
                tooltip="connectivity status"
                ariaLabel="connectivity status: whatstatus"
                onClickTooltipButton={(event: MouseEvent) => {
                    onClickConnectivityStatus && onClickConnectivityStatus(event);
                }}
            />
        )}
        {languageSelector && <LanguageSelector onClickLanguageSelector={onClickLanguageSelector} keyboardShortcut={keyboardShortcut} />}
        {voiceCommand && (
            <TooltipButton
                icon="mic"
                tooltip="start/stop voice commands"
                ariaLabel="start/stop voice command"
                testId="voiceCommand"
                onClickTooltipButton={(event: MouseEvent) => {
                    onClickVoiceCommand && onClickVoiceCommand(event);
                }}
            />
        )}
        {themeSwitch && (
            <TooltipButton
                icon="wb_sunny"
                tooltip="switch to dark/light theme"
                ariaLabel="switch to dark/light theme"
                testId="themeSwitch"
                onClickTooltipButton={(event: MouseEvent) => {
                    switchTheme(event);
                    onThemeSwitch && onThemeSwitch(event);
                }}
            />
        )}
        {secondWindow && (
            <TooltipButton
                icon="window_expand"
                tooltip="second window"
                ariaLabel="second window"
                testId="secondWindow"
                onClickTooltipButton={(event: MouseEvent) => {
                    onClickSecondWindow && onClickSecondWindow(event);
                }}
            />
        )}
        {keyboardShortcut && (
            <TooltipButton
                icon="keyboard"
                tooltip="Keyboard shortcut"
                ariaLabel="keyboard shortcut"
                testId="keyboardShortcut"
                onClickTooltipButton={(event: MouseEvent) => {
                    onClickKeyboardShortcut && onClickKeyboardShortcut(event);
                    handleKeyboardShortcutsClick();
                }}
                ref={keyboardShortcutButtonRef}
            />
        )}
        {settings && (
            <TooltipButton
                icon="settings"
                tooltip="settings"
                ariaLabel="settings"
                testId="settings"
                onClickTooltipButton={(event: MouseEvent) => {
                    onClickSettings && onClickSettings(event);
                    handleSettingsClick();
                }}
                ref={settingsButtonRef}
            />
        )}
        {help && (<TooltipButton
            icon="help"
            tooltip="Help"
            ariaLabel="help"
            testId="help"
            onClickTooltipButton={(event: MouseEvent) => {
                onClickHelp && onClickHelp(event);
                handleHelpClick();
            }}
            keyboardShortcut
            ref={helpButtonRef}
        />
        )}
        {globalNavigation && (<TooltipButton
            icon="apps"
            tooltip="Global Navigation"
            ariaLabel="global navigation"
            testId="global-navigation"
            onClickTooltipButton={(event: MouseEvent) => {
                onClickGlobalNavigation && onClickGlobalNavigation(event);
                handleGlobalNavigationClick();
            }}
            ref={globalNavigationButtonRef}
        />
        )}
        {username && <Avatar username={username} onClickAvatar={onClickAvatar} />}
    </HeaderSubContainerRight>
}

export default RightContainer;